import { css } from '@emotion/react';
import {
  faCheck,
  faTrashCan,
} from '@fortawesome/pro-solid-svg-icons';
import React, {
  type ComponentProps,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { ButtonComponent } from '~/_shared/baseComponents/buttons/button/button.component';
import { ButtonStyle } from '~/_shared/baseComponents/buttons/button/button.types';
import { type ModalProps } from '../../../modal/modalType.enum';
import {
  LottieAnimationComponent,
  LottieAnimationTypes,
  useLottieAnimationDefaultColors,
} from '../../baseComponents/lottieAnimation';
import { getLottieAnimationDuration } from '../../baseComponents/lottieAnimation/lottieAnimation.helpers';
import { useTranslation } from '../../utils/hooks';
import { ModalComponent } from '../modal/modal.component';

const ANIMATION_SIZE = 40;

const centerStyle = css({
  alignItems: 'center',
  display: 'flex',
  justifyContent: 'center',
});

const contentWrapperStyle = css({
  padding: '17px 33px',
});

export type ChangesPendingModalProps = ModalProps<{
  onDiscardChanges: () => void;
  onSaveChanges: () => void;
} & Pick<ComponentProps<typeof ModalComponent>, 'shouldReturnFocusAfterClose'>>;

export const ChangesPendingModalComponent: React.FC<ChangesPendingModalProps> = ({
  onDiscardChanges,
  onSaveChanges,
  isOpen,
  onClose,
  shouldReturnFocusAfterClose,
}) => {
  const [playSuccessAnimation, setPlaySuccessAnimation] = useState(false);
  const [playTrashAnimation, setPlayTrashAnimation] = useState(false);
  const [t] = useTranslation();
  const animationColors = useLottieAnimationDefaultColors();

  const handleSave = useCallback(() => {
    onSaveChanges();
    setPlaySuccessAnimation(true);
  }, [onSaveChanges]);

  const handleDiscardButton = useCallback(() => {
    onDiscardChanges();
    setPlayTrashAnimation(true);
  }, [onDiscardChanges]);

  const modalBody = useMemo(() => {
    if (playSuccessAnimation) {
      return (
        <div css={centerStyle}>
          <LottieAnimationComponent
            colors={animationColors.Success}
            type={LottieAnimationTypes.Success}
            size={ANIMATION_SIZE}
            autoplay
          />
        </div>
      );
    }

    if (playTrashAnimation) {
      return (
        <div css={centerStyle}>
          <LottieAnimationComponent
            colors={animationColors.Delete}
            type={LottieAnimationTypes.Delete}
            size={ANIMATION_SIZE}
            autoplay
          />
        </div>
      );
    }

    return (
      <div>
        {t('ChangesPendingMessage')}
      </div>
    );
  }, [animationColors.Delete, animationColors.Success, playSuccessAnimation, playTrashAnimation, t]);

  useEffect(() => {
    if (!playSuccessAnimation && !playTrashAnimation) {
      return;
    }

    let timeoutId: number | null = null;
    if (playSuccessAnimation) {
      const animationDuration = getLottieAnimationDuration(LottieAnimationTypes.Success);
      timeoutId = window.setTimeout(() => {
        setPlaySuccessAnimation(false);
        onClose();
      }, animationDuration);
    }
    else if (playTrashAnimation) {
      const animationDuration = getLottieAnimationDuration(LottieAnimationTypes.Delete);
      timeoutId = window.setTimeout(() => {
        setPlayTrashAnimation(false);
        onClose();
      }, animationDuration);
    }

    return () => {
      if (timeoutId !== null) {
        clearTimeout(timeoutId);
      }
    };
  }, [playSuccessAnimation, playTrashAnimation, onClose]);

  const disabled = playSuccessAnimation || playTrashAnimation;

  return (
    <ModalComponent
      shouldReturnFocusAfterClose={shouldReturnFocusAfterClose}
      caption={t('Changes Pending')}
      confirmButton={(
        <ButtonComponent
          buttonStyle={ButtonStyle.Primary}
          isDisabled={disabled}
          onClick={handleSave}
          prefixIcon={faCheck}
          text={t('Save')}
        />
      )}
      contentStyle={contentWrapperStyle}
      isOpen={isOpen}
      leftFooterContent={(
        <ButtonComponent
          buttonStyle={ButtonStyle.Danger}
          isDisabled={disabled}
          prefixIcon={faTrashCan}
          text={t('Trash Em')}
          onClick={handleDiscardButton}
        />
      )}
      onClose={onClose}
    >
      {modalBody}
    </ModalComponent>
  );
};
